import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import type { BoardCard } from '@nightshift/ui-types';

import { BoardSelectionContext } from './board-selection-context.ts';
import { groupMenuItems } from '../lib/board-selection.ts';
import { api } from '../live/api.ts';
import { useStore } from '../live/context.tsx';
import {
  CARD_MENU_MIN_WIDTH,
  layoutBoxFromClientRect,
  layoutPx,
  placeAnchoredMenu,
} from '../lib/anchored-menu.ts';
import { activeElapsed, isQueuedCard, shippingLabel } from '../lib/card-status.ts';
import { CardArtifacts } from './card-artifacts.tsx';
import { cardExcerpt } from '../lib/card-excerpt.ts';
import { cardMenuItems, type CardMenuAction, type CardMenuItem } from '../lib/board-actions.ts';
import {
  BOARD_DRAG_MIME,
  BOARD_HOLD_REASON,
  boardDragTargets,
  type BoardDragPayload,
} from '../lib/board-drag.ts';
import { uiScale, useTimeFormat, useActivityDisplay } from '../lib/display.ts';
import { absoluteTime, ciStatusWords, observedTime } from '../lib/format.ts';
import { isHumanState, QUEUED_STYLE, stateStyle } from '../lib/palette.ts';
import { canDeploy } from '../lib/privilege.ts';
import type { SessionUser } from '../live/api.ts';

function positionCardMenu(
  button: HTMLElement,
  panel: HTMLElement | null,
  itemCount: number,
): { top: number; left: number } {
  const scale = uiScale();
  return placeAnchoredMenu({
    anchor: layoutBoxFromClientRect(button.getBoundingClientRect(), scale),
    menuWidth: panel?.offsetWidth ?? CARD_MENU_MIN_WIDTH,
    menuHeight: panel?.offsetHeight ?? 10 + itemCount * 36,
    viewportWidth: layoutPx(window.innerWidth, scale),
    viewportHeight: layoutPx(window.innerHeight, scale),
  });
}

function positionCardMenuAtPoint(
  clientX: number,
  clientY: number,
  panel: HTMLElement | null,
  itemCount: number,
): { top: number; left: number } {
  const scale = uiScale();
  const x = layoutPx(clientX, scale);
  const y = layoutPx(clientY, scale);
  return placeAnchoredMenu({
    anchor: { top: y, left: x, right: x, bottom: y },
    menuWidth: panel?.offsetWidth ?? CARD_MENU_MIN_WIDTH,
    menuHeight: panel?.offsetHeight ?? 10 + itemCount * 36,
    viewportWidth: layoutPx(window.innerWidth, scale),
    viewportHeight: layoutPx(window.innerHeight, scale),
  });
}

export interface BoardCardViewProps {
  card: BoardCard;
  stack?: readonly BoardCard[];
  generatedAt: number;
  now: number;
  onOpen: (key: string) => void;
  draggingKey?: string | null;
  onDragPayload?: (payload: BoardDragPayload | null) => void;
  user?: SessionUser | null;
  deployConfigured?: boolean;
}

function repairLabel(card: BoardCard): string | null {
  let used = 0;
  let max = 5;
  for (const row of card.repairs) {
    if (row.used > used) {
      used = row.used;
      max = row.max;
    }
  }
  if (used === 0) return null;
  return `${used}/${max}`;
}

/** The compact run count shown in the card header. */
function runsLabel(card: BoardCard): string {
  return `${card.attemptCount} ${card.attemptCount === 1 ? 'run' : 'runs'}`;
}

function runsTitle(card: BoardCard): string {
  const failed = card.failedAttemptCount > 0 ? `, ${card.failedAttemptCount} failed` : '';
  return `${card.attemptCount} attempt${card.attemptCount === 1 ? '' : 's'}${failed}`;
}

/**
 * Dark Factory board card. Per-state extras that the snapshot leaves null stay omitted —
 * do not invent a CI spinner or MR link the card does not carry.
 */
export function BoardCardView({
  card,
  stack = [card],
  generatedAt,
  now,
  onOpen,
  draggingKey,
  onDragPayload,
  user = null,
  deployConfigured = false,
}: BoardCardViewProps) {
  const store = useStore();
  const selection = useContext(BoardSelectionContext);
  const selected = selection?.cards.some((entry) => entry.ticketKey === card.ticketKey) ?? false;
  const actionCards = selected ? selection!.cards : [card];
  const format = useTimeFormat();
  const activityMode = useActivityDisplay();
  const shipping = shippingLabel(card, now);
  const ciWaiting = card.state === 'GitLab CI' || (card.state === 'Merging' && card.landing?.outcome === 'waiting');
  const [menu, setMenu] = useState(false);
  const [menuOrigin, setMenuOrigin] = useState<'button' | { x: number; y: number }>('button');
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
  const [busy, setBusy] = useState<CardMenuAction | null>(null);
  const dragged = useRef(false);
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const [menuEl, setMenuEl] = useState<HTMLDivElement | null>(null);
  // A queued card wears indigo rather than the Waiting amber: it is parked behind other work
  // and will start itself, so it is news, not a request.
  const queued = isQueuedCard(card);
  const style = queued ? QUEUED_STYLE : stateStyle(card.state);
  const human = isHumanState(card.state);
  const items = groupMenuItems(actionCards, {
    isAdmin: canDeploy(user),
    deployConfigured,
    deployable: card.deployable,
    dependsOn: card.dependsOn,
  });
  const repair = repairLabel(card);
  const excerpt = card.displaySummary ?? cardExcerpt(card.description);
  const updatedAt = generatedAt - card.elapsedMs;
  const age = observedTime(updatedAt, now, format);
  const canDrag = boardDragTargets(card.state).length > 0 && busy === null;
  const isDragging = draggingKey === card.ticketKey;

  useLayoutEffect(() => {
    if (!menu) {
      setMenuPos(null);
      return;
    }
    const btn = menuBtnRef.current;
    if (menuOrigin !== 'button') {
      setMenuPos(positionCardMenuAtPoint(menuOrigin.x, menuOrigin.y, menuEl, items.length));
      return;
    }
    if (!btn) return;
    setMenuPos(positionCardMenu(btn, menuEl, items.length));
  }, [menu, menuEl, items.length, menuOrigin]);

  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(false);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        close();
      }
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [menu]);

  const runAction = async (item: CardMenuItem): Promise<void> => {
    const action = item.action;
    // Queue is the one row that carries a target. Without it there is nothing to wait for,
    // so it is not offered; this is the type narrowing, not a fallback.
    setMenu(false);
    setBusy(action);
    try {
      const results = await Promise.allSettled(actionCards.map(async (target) => {
        const key = target.ticketKey;
        const queueBehind = action === 'queue' ? cardMenuItems(target.state, { dependsOn: target.dependsOn }).find((entry) => entry.action === 'queue')?.blocker : undefined;
        if (action === 'queue' && queueBehind === undefined) throw new Error(`No queue target for ${key}`);
        const result =
          action === 'rescan'
            ? await api.rescanTicket(key)
            : action === 'start'
            ? await api.start(key)
            : action === 'research'
              ? await api.start(key, { activity: 'Research' })
              : action === 'planning'
                ? await api.start(key, { activity: 'Planning' })
                : queueBehind !== undefined
                  ? await api.sequenceAfter(key, { blocker: queueBehind })
                  : action === 'record_done'
                    ? await api.recordDone(key)
                    : action === 'shelve'
                      ? await api.shelve(key)
                      : action === 'hold'
                        ? await api.wait(key, { reason_type: 'manual_hold', reason: BOARD_HOLD_REASON })
                        : action === 'cancel'
                          ? await api.cancel(key)
                          : action === 'restore'
                            ? await api.restore(key)
                            : action === 'deploy'
                              ? await api.deploy(key)
                              : await api.resume(key);
        return { ...result, message: result.message ?? `${key}: ${item.label} completed.` };
      }));
      for (const result of results) {
        if (result.status === 'fulfilled') store.toast('good', result.value.message);
        else store.toast('bad', String(result.reason));
      }
      await store.refetch();
    } catch (error) {
      store.toast('bad', error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(null);
    }
  };

  return (
    <article
      className={`tcard${selected ? ' is-selected' : ''}${busy ? ' tcard-busy' : ''}${canDrag ? ' is-draggable' : ''}${isDragging ? ' is-dragging' : ''}`}
      role="button"
      aria-pressed={selection ? selected : undefined}
      tabIndex={0}
      draggable={canDrag}
      onDragStart={(event) => {
        if (!canDrag) {
          event.preventDefault();
          return;
        }
        dragged.current = true;
        const payload: BoardDragPayload = { ticketKey: card.ticketKey, fromState: card.state, tickets: actionCards.map((entry) => ({ ticketKey: entry.ticketKey, fromState: entry.state })) };
        event.dataTransfer.setData(BOARD_DRAG_MIME, JSON.stringify(payload));
        event.dataTransfer.setData('text/plain', card.ticketKey);
        event.dataTransfer.effectAllowed = 'move';
        onDragPayload?.(payload);
      }}
      onDragEnd={() => {
        onDragPayload?.(null);
      }}
      onClick={(event) => {
        if (dragged.current) {
          dragged.current = false;
          return;
        }
        selection?.select(card, stack, event);
        if (selection && (event.ctrlKey || event.metaKey || event.shiftKey)) return;
        onOpen(card.ticketKey);
      }}
      onContextMenu={(event) => {
        if (items.length === 0 || busy !== null) return;
        event.preventDefault();
        event.stopPropagation();
        setMenuOrigin({ x: event.clientX, y: event.clientY });
        setMenu(true);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen(card.ticketKey);
        }
      }}
      style={{
        boxShadow: human
          ? `inset 3px 0 0 ${style.line}, inset 0 0 0 1px ${style.edge}`
          : `inset 3px 0 0 ${style.line}, inset 0 0 0 1px var(--hairline)`,
        background: human ? style.wash : 'var(--surface)',
      }}
      aria-label={`${card.ticketKey} — ${card.title}, ${card.state}`}
    >
      <header className="tcard-head">
        {human ? (
          <span
            className="lamp lamp-breathe"
            style={{ background: style.line, boxShadow: `0 0 8px ${style.line}` }}
            aria-hidden
          />
        ) : null}
        <span className="mono tcard-key" style={{ color: human ? style.text : 'var(--text-2)' }}>
          {card.ticketKey}
        </span>
        <span className="num tcard-age" title={absoluteTime(updatedAt)}>
          {age}
        </span>
        {card.attemptCount > 0 ? (
          <span
            className={`num tcard-runs${card.failedAttemptCount >= 2 ? ' is-warn' : ''}`}
            title={runsTitle(card)}
            aria-label={runsTitle(card)}
          >
            {runsLabel(card)}
          </span>
        ) : null}
        {items.length > 0 ? (
          <span className="tcard-menu-wrap">
            <button
              ref={menuBtnRef}
              type="button"
              className="tcard-menu-btn"
              aria-label={`Actions for ${card.ticketKey}`}
              aria-haspopup="menu"
              aria-expanded={menu}
              disabled={busy !== null}
              draggable={false}
              onClick={(event) => {
                event.stopPropagation();
                setMenuOrigin('button');
                setMenu((open) => !open);
              }}
            >
              <span aria-hidden>···</span>
            </button>
            {menu
              ? createPortal(
                  <>
                    <div
                      className="tcard-menu-scrim"
                      role="presentation"
                      onClick={(event) => {
                        event.stopPropagation();
                        setMenu(false);
                      }}
                    />
                    <div
                      ref={setMenuEl}
                      className="menu tcard-menu"
                      role="menu"
                      aria-label={actionCards.length > 1 ? `${actionCards.length} selected tickets actions` : `${card.ticketKey} actions`}
                      onKeyDown={(event) => {
                        event.stopPropagation();
                        if (event.key === 'Escape') { setMenu(false); menuBtnRef.current?.focus(); }
                      }}
                      style={
                        menuPos
                          ? { top: menuPos.top, left: menuPos.left }
                          : { visibility: 'hidden', top: 0, left: 0 }
                      }
                    >
                      {actionCards.length > 1 ? <div className="tcard-selection-count">{actionCards.length} selected tickets</div> : null}
                      {items.map((item) => (
                        <button
                          key={item.action}
                          type="button"
                          className="menu-item"
                          role="menuitem"
                          title={item.hint}
                          onClick={(event) => {
                            event.stopPropagation();
                            void runAction(item);
                          }}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </>,
                  document.body,
                )
              : null}
          </span>
        ) : null}
      </header>

      <p className="tcard-title clamp-2" title={card.displayTitle ? `Original title: ${card.title}` : undefined}>{card.displayTitle ?? card.title}</p>
      {excerpt ? <p className="tcard-desc">{excerpt}</p> : null}

      {card.activeSince != null && !card.stalled && now - generatedAt < 95_000 ? (
        <div className="tcard-activity" aria-label={`Active run, ${activeElapsed(card.activeSince, now)} elapsed`}>
          {activityMode === 'throbber' ? <span className="tcard-throbber" aria-hidden /> : <span className="mono num">{activeElapsed(card.activeSince, now)}</span>}
          <span>Running</span>
        </div>
      ) : null}
      {ciWaiting ? (
        <section className="tcard-ci" aria-label="CI progress">
          <div className="tcard-ci-head"><strong>GitLab CI</strong><span>{card.ci ? ciStatusWords(card.ci.status) : 'Awaiting pipeline'}</span></div>
          {card.ci ? <>
            {card.ci.pipelineId > 0 ? <a href={card.ci.url} target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}>Pipeline #{card.ci.pipelineId} · {card.ci.sha.slice(0, 8)}</a> : null}
            {card.ci.jobs.slice(0, 8).map((job) => <div key={job.id} className="tcard-ci-job">
              <span title={`${job.stage} · ${job.name}`}>{job.stage} · {job.name}</span>
              <span title={job.status}>{ciStatusWords(job.status)}</span>
            </div>)}
            {card.ci.jobs.length > 8 ? <span>{card.ci.jobs.length - 8} more jobs in GitLab</span> : null}
            {card.ci.jobs.length === 0 ? <span>Job details not yet available</span> : null}
            <small>{card.ci.error ? `${card.ci.error} · ` : (now - card.ci.observedAt > 120_000 ? 'Update overdue · ' : 'Updated ')}{observedTime(card.ci.observedAt, now, format)}</small>
          </> : <span>Waiting for a pipeline on the current branch head.</span>}
        </section>
      ) : null}
      <div className="tcard-chips">
        <span
          className="chip"
          style={{ background: style.wash, color: style.text, boxShadow: `inset 0 0 0 1px ${style.edge}` }}
        >
          {/* "Queued", not "Waiting": the state is Waiting, but nothing here is waiting on a
              person, and the chip beside this one already says what it is waiting for. */}
          {queued ? 'Queued' : card.activity ?? card.state}
        </span>
        {card.stalled ? (
          // Deliberately the first chip after the state, and deliberately not the whole
          // sentence: the card says "look at me", the drawer says why.
          <span
            className="chip tcard-stalled"
            style={{ background: 'var(--warn-wash)', color: 'var(--warn-text)', boxShadow: 'inset 0 0 0 1px var(--warn-edge)' }}
            title={card.stalled}
          >
            Stalled
          </span>
        ) : null}
        {card.landing && !card.stalled && card.state !== 'Done' ? (
          <span className="chip tcard-landing" title={card.landing.detail}>
            {card.landing.outcome === 'waiting' ? 'waiting for CI' : card.landing.outcome}
          </span>
        ) : null}
        {card.waitingReasonText ? (
          <span
            className="chip tcard-wait"
            style={
              queued
                ? { background: QUEUED_STYLE.wash, color: QUEUED_STYLE.text }
                : { background: 'var(--accent-wash)', color: 'var(--accent-bright)' }
            }
          >
            {card.waitingReasonText}
          </span>
        ) : null}
        {/*
          "Depends on", not "waiting for": the card says what is true of the work, and only a
          ticket actually parked in Waiting gets that word. Satisfied blockers never reach the
          card, so anything shown here is still outstanding.
        */}
        {card.dependsOn.length > 0 ? (
          <span
            className={`chip tcard-depends${card.dependsOn.some((row) => row.chip === 'unavailable') ? ' is-unavailable' : ''}`}
            title={card.dependsOn
              .map((row) => `${row.ticketKey} · ${row.state} · ${row.title}`)
              .join('\n')}
          >
            {card.dependsOn.length === 1
              ? `depends on ${card.dependsOn[0]?.ticketKey ?? ''}`
              : `depends on ${card.dependsOn.length}`}
          </span>
        ) : null}
        {card.blocksCount > 0 ? (
          <span className="chip tcard-blocks" title="Open tickets waiting on this one">
            blocks {card.blocksCount}
          </span>
        ) : null}
        {/*
          A decision is waiting, not a fact about the work. It sits after the confirmed
          dependency chips on purpose: what is recorded outranks what is guessed.
        */}
        {card.suggestedCount > 0 ? (
          <span
            className="chip tcard-suggested"
            title="Dependency findings requiring a decision"
          >
            {`${card.suggestedCount} ${card.suggestedCount === 1 ? 'needs' : 'need'} attention`}
          </span>
        ) : null}
        {card.notScanned ? (
          <span
            className="chip tcard-notscanned"
            title="Started before anything compared this ticket against its siblings"
          >
            not scanned
          </span>
        ) : null}
        {card.mr ? (
          <a
            className="chip tcard-mr"
            href={card.mr.url}
            target="_blank"
            rel="noreferrer"
            draggable={false}
            onClick={(event) => event.stopPropagation()}
          >
            MR {card.mr.iid}
          </a>
        ) : null}
        {repair ? (
          <span className="chip num" title="Repair returns used of the family budget">
            repair {repair}
          </span>
        ) : null}
        {shipping ? (
          <span className={`chip tcard-shipping is-${shipping.tone}`} title={shipping.detail}>
            <span className="lamp" aria-hidden="true" />
            {shipping.label}
          </span>
        ) : null}
        {/*
          Bottom right, after every chip: what the pipeline built is a thing to fetch, not a
          state to read, so it sits where a person reaches for it rather than in the rail of
          facts about the ticket.
        */}
        {card.artifacts ? <CardArtifacts ticketKey={card.ticketKey} artifacts={card.artifacts} now={now} /> : null}
      </div>
    </article>
  );
}
